const CROCKFORD_BASE32 = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const MAX_ULID_TIMESTAMP = 281_474_976_710_655;

export interface CryptoRandomSource {
  getRandomValues(bytes: Uint8Array<ArrayBuffer>): Uint8Array<ArrayBuffer>;
}

export interface ConsentIdOptions {
  readonly now?: () => number;
  readonly crypto?: CryptoRandomSource;
}

export type ConsentIdErrorCode =
  | 'insecure_random_unavailable'
  | 'invalid_timestamp'
  | 'random_generation_failed';

export class ConsentIdError extends Error {
  readonly code: ConsentIdErrorCode;

  constructor(code: ConsentIdErrorCode, message: string) {
    super(message);
    this.name = 'ConsentIdError';
    this.code = code;
  }
}

function resolveCrypto(candidate: CryptoRandomSource | undefined): CryptoRandomSource {
  if (candidate) {
    return candidate;
  }

  if (
    typeof globalThis.crypto !== 'undefined' &&
    typeof globalThis.crypto.getRandomValues === 'function'
  ) {
    return globalThis.crypto;
  }

  throw new ConsentIdError(
    'insecure_random_unavailable',
    'Consent IDs require crypto.getRandomValues; weak randomness is not allowed.',
  );
}

function encodeTimestamp(timestamp: number): string {
  const characters = new Array<string>(10);
  let remaining = timestamp;

  for (let index = characters.length - 1; index >= 0; index -= 1) {
    characters[index] = CROCKFORD_BASE32[remaining % 32] ?? '0';
    remaining = Math.floor(remaining / 32);
  }

  return characters.join('');
}

function encodeRandomBytes(bytes: Uint8Array): string {
  let buffer = 0;
  let bitCount = 0;
  let encoded = '';

  for (const byte of bytes) {
    buffer = buffer * 256 + byte;
    bitCount += 8;

    while (bitCount >= 5) {
      bitCount -= 5;
      const divisor = 2 ** bitCount;
      const index = Math.floor(buffer / divisor);
      encoded += CROCKFORD_BASE32[index] ?? '0';
      buffer %= divisor;
    }
  }

  return encoded;
}

export function createConsentId(options: ConsentIdOptions = {}): string {
  const timestamp = (options.now ?? Date.now)();

  if (
    !Number.isSafeInteger(timestamp) ||
    timestamp < 0 ||
    timestamp > MAX_ULID_TIMESTAMP
  ) {
    throw new ConsentIdError(
      'invalid_timestamp',
      'Consent ID timestamp must fit the 48-bit ULID timestamp range.',
    );
  }

  const randomBytes = new Uint8Array(10);

  try {
    resolveCrypto(options.crypto).getRandomValues(randomBytes);
  } catch (error) {
    if (error instanceof ConsentIdError) {
      throw error;
    }

    throw new ConsentIdError(
      'random_generation_failed',
      'Cryptographic consent ID generation failed.',
    );
  }

  return `gcs_${encodeTimestamp(timestamp)}${encodeRandomBytes(randomBytes)}`;
}
